import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Shield, FileText, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Document, Page, pdfjs } from "react-pdf";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface Policy {
  id: string;
  policy_type: string;
  file_url: string;
  file_name: string;
}

interface PolicyAcknowledgementDialogProps {
  open: boolean;
  policies: Policy[];
  onAcknowledge: (policyIds: string[]) => void;
  isAcknowledging: boolean;
}

export const PolicyAcknowledgementDialog = ({
  open,
  policies,
  onAcknowledge,
  isAcknowledging,
}: PolicyAcknowledgementDialogProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [agreedPolicies, setAgreedPolicies] = useState<Record<string, boolean>>({});
  const [pledgeChecked, setPledgeChecked] = useState(false);
  const [viewingPolicy, setViewingPolicy] = useState<Policy | null>(null);
  const [policyUrl, setPolicyUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);

  useEffect(() => {
    return () => {
      if (policyUrl) {
        window.URL.revokeObjectURL(policyUrl);
      }
    };
  }, [policyUrl]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const getPolicyLabel = (policyType: string) => {
    switch (policyType) {
      case "code_of_ethics":
        return t("codeOfEthics");
      case "code_of_conduct":
        return t("codeOfConduct");
      case "gdpr_policy":
        return t("gdprPolicy");
      default:
        return policyType;
    }
  };

  const handleDownload = async (policy: Policy) => {
    try {
      const { data, error } = await supabase.storage
        .from("company-policies")
        .download(policy.file_url);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = policy.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleView = async (policy: Policy) => {
    try {
      const { data, error } = await supabase.storage
        .from("company-policies")
        .download(policy.file_url);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      setPolicyUrl(url);
      setViewingPolicy(policy);
      setPageNumber(1);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const allPoliciesAgreed =
    policies.length > 0 &&
    policies.every((policy) => agreedPolicies[policy.id]) &&
    pledgeChecked;

  const handleAccept = () => {
    if (allPoliciesAgreed) {
      onAcknowledge(policies.map((p) => p.id));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-4xl max-h-[90vh] p-0"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="p-6 pb-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold">
                  {t("policiesRequireAcknowledgement")}
                </DialogTitle>
                <DialogDescription className="text-base mt-1">
                  {t("pleaseReviewPolicies")}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="px-6 py-4 max-h-[50vh]">
            <div className="space-y-4">
              {policies.map((policy) => (
                <div
                  key={policy.id}
                  className="p-5 bg-primary/5 border-2 border-primary/20 rounded-lg"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-primary" />
                      <h3 className="text-lg font-semibold">
                        {getPolicyLabel(policy.policy_type)}
                      </h3>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleView(policy)}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        {t("viewPolicy")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(policy)}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {t("downloadPolicy")}
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {policy.file_name}
                  </p>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id={`policy-${policy.id}`}
                      checked={agreedPolicies[policy.id] || false}
                      onCheckedChange={(checked) =>
                        setAgreedPolicies((prev) => ({
                          ...prev,
                          [policy.id]: checked as boolean,
                        }))
                      }
                    />
                    <label
                      htmlFor={`policy-${policy.id}`}
                      className="text-sm font-medium cursor-pointer"
                    >
                      {t("iHaveReadAndAgree")} {getPolicyLabel(policy.policy_type)}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          <DialogFooter className="p-6 pt-0 flex-col sm:flex-col gap-4">
            <div className="flex items-start gap-3 w-full p-4 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border-2 border-yellow-500">
              <Checkbox
                id="pledge-comply"
                checked={pledgeChecked}
                onCheckedChange={(checked) => setPledgeChecked(checked as boolean)}
                className="mt-1"
              />
              <label
                htmlFor="pledge-comply"
                className="text-sm font-bold leading-relaxed cursor-pointer text-yellow-900 dark:text-yellow-100"
              >
                {t("iPledgeToComply")}
              </label>
            </div>

            <Button
              onClick={handleAccept}
              disabled={!allPoliciesAgreed || isAcknowledging}
              className="w-full"
              size="lg"
            >
              {t("acceptAndAcknowledge")}
            </Button>

            <p className="text-xs text-center text-muted-foreground w-full">
              {t("mustAcknowledgePolicies")}
            </p>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewingPolicy} onOpenChange={() => {
        setViewingPolicy(null);
        setPolicyUrl(null);
        setNumPages(null);
        setPageNumber(1);
      }}>
        <DialogContent className="max-w-full sm:max-w-4xl h-[95vh] sm:h-[90vh] flex flex-col p-3 sm:p-6">
          <DialogHeader className="pb-3">
            <DialogTitle className="text-base sm:text-lg">
              {viewingPolicy && getPolicyLabel(viewingPolicy.policy_type)}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto min-h-0 flex flex-col items-center bg-muted/30 rounded-lg p-2">
            {policyUrl && (
              <>
                <div className="w-full flex justify-center">
                  <Document
                    file={policyUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<div className="text-center py-8 text-sm">Loading PDF...</div>}
                    error={<div className="text-center py-8 text-destructive text-sm">Error loading PDF</div>}
                  >
                    <Page 
                      pageNumber={pageNumber}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      width={window.innerWidth < 640 ? window.innerWidth - 40 : undefined}
                      className="max-w-full shadow-lg"
                    />
                  </Document>
                </div>
                {numPages && numPages > 1 && (
                  <div className="mt-4 flex items-center gap-2 sm:gap-4 pb-4 sticky bottom-0 bg-background/95 backdrop-blur-sm p-3 rounded-lg border shadow-lg">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))}
                      disabled={pageNumber <= 1}
                      className="touch-manipulation"
                    >
                      <ChevronLeft className="h-4 w-4 sm:mr-1" />
                      <span className="hidden sm:inline">Previous</span>
                    </Button>
                    <span className="text-xs sm:text-sm font-medium whitespace-nowrap px-2">
                      {pageNumber} / {numPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(prev => Math.min(prev + 1, numPages))}
                      disabled={pageNumber >= numPages}
                      className="touch-manipulation"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <ChevronRight className="h-4 w-4 sm:ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
